import * as React from 'react';
import { Dialog } from "@progress/kendo-react-dialogs";
import { getCompanyUrlList, addCompanyUrl, updateCompanyUrl, removeCompanyUrl } from '../../ApiUtils/ApiUtils';
import { UserProfileContext } from '../Console/ConsoleLayout';

export type CompanyUrl = {
    id: number;
    companyName: string;
    urlName: string;
    urlType: string;
    url: string;
    createdBy: number;
    createdAt: Date;
    updatedBy: number;
    updatedAt: Date;
};

export type CompanyUrlsFormState = {
    companyUrlList: CompanyUrl[];
    targetCompanyUrl: CompanyUrl;
    isAdmin: boolean;
    aspUserId: number;
    targetCompanyName: string;
    isEditing: boolean;
    showDeleteConfirm: boolean;
    deleteTargetId: number | null;
}

type CompanyUrlsFormProps = {
    companyName: string;
    toggleDialog: () => void;
}

class CompanyUrlsForm extends React.Component<CompanyUrlsFormProps, CompanyUrlsFormState> {
    static contextType = UserProfileContext;

    public constructor(props: CompanyUrlsFormProps) {
        super(props);
        this.state = {
            companyUrlList: [],
            targetCompanyUrl: this.createEmptyUrl(),
            isAdmin: false,
            aspUserId: 0,
            targetCompanyName: '',
            isEditing: false,
            showDeleteConfirm: false,
            deleteTargetId: null
        };
    }

    createEmptyUrl = (): CompanyUrl => ({
        id: 0,
        companyName: this.props.companyName,
        urlName: '',
        urlType: '',
        url: '',
        createdBy: 0,
        createdAt: new Date(),
        updatedBy: 0,
        updatedAt: new Date()
    });

    componentDidMount() {
        getCompanyUrlList(this.props.companyName).then((response) => {
            const sortedCompanyUrls = response.data.companyUrls
                .sort((a: CompanyUrl, b: CompanyUrl) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

            this.setState({
                companyUrlList: sortedCompanyUrls,
                isAdmin: this.context?.permissions?.admin || this.context?.permissions?.root,
                aspUserId: this.context?.aspUserId,
            });
        }).catch(err => console.error("Error fetching company URLs:", err));
    }

    handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        this.setState(prev => ({
            targetCompanyUrl: {
                ...prev.targetCompanyUrl,
                [name]: value
            }
        }));
    };

    handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const { targetCompanyUrl, companyUrlList, aspUserId, isEditing } = this.state;

        // Duplicate check
        const duplicate = companyUrlList.some(url =>
            url.url.toLowerCase() === targetCompanyUrl.url.toLowerCase() &&
            url.id !== targetCompanyUrl.id
        );

        if (duplicate) {
            alert("This URL already exists.");
            return;
        }

        if (isEditing) {
            updateCompanyUrl({ ...targetCompanyUrl, updatedBy: aspUserId })
                .then((res) => {
                    if (res && res.data && res.data.companyUrl) {
                        this.setState(prev => ({
                            companyUrlList: prev.companyUrlList.map(url =>
                                url.id === targetCompanyUrl.id ? { ...targetCompanyUrl } : url
                            ),
                            targetCompanyUrl: this.createEmptyUrl(),
                            isEditing: false
                        }));
                    }
                })
                .catch((error) => {
                    console.error('Error updating company URL:', error);
                });
        } else {
            addCompanyUrl({ ...targetCompanyUrl, createdBy: aspUserId })
                .then((res) => {
                    if (res && res.data && res.data.companyUrl) {
                        this.setState(prev => ({
                            companyUrlList: [...prev.companyUrlList, res.data.companyUrl],
                            targetCompanyUrl: this.createEmptyUrl()
                        }));
                    }
                })
                .catch((error) => {
                    console.error('Error adding company URL:', error);
                });
        }
    }

    handleEdit = (url: CompanyUrl) => {
        this.setState({
            targetCompanyUrl: { ...url },
            isEditing: true
        });
    }

    handleDelete = (id: number) => {
      this.setState({
      showDeleteConfirm: true,
      deleteTargetId: id
        });
    }
    confirmDelete = () => {
    const { deleteTargetId } = this.state;
    if (deleteTargetId !== null) {
        removeCompanyUrl(deleteTargetId)
            .then(() => {
                this.setState(prev => ({
                    companyUrlList: prev.companyUrlList.filter(url => url.id !== deleteTargetId),
                    showDeleteConfirm: false,
                    deleteTargetId: null
                }));
            })
            .catch((error) => {
                console.error('Error deleting company URL:', error);
                this.setState({ showDeleteConfirm: false, deleteTargetId: null });
            });
                }
            };

    cancelDelete = () => {
        this.setState({ showDeleteConfirm: false, deleteTargetId: null });
    };

        public render() {
            const { targetCompanyUrl, companyUrlList, isEditing } = this.state;

            return (
                <>
                    <Dialog
                        title={this.props.companyName + " - URL Configuration"}
                        onClose={this.props.toggleDialog}
                        style={{ fontSize: "8px" }}
                        width={"75%"}
                        height={"100%"}
                    >
                        <form onSubmit={this.handleSubmit} style={{ fontSize: "11px", padding: "10px 15px 0px" }}>
                            {/* URL Name */}
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                                <label style={{ width: '100px', marginRight: '10px' }}>URL Name:</label>
                                <input
                                    type="text"
                                    name="urlName"
                                    value={targetCompanyUrl.urlName}
                                    onChange={this.handleInputChange}
                                    required
                                    style={{ width: '250px' }}
                                />
                            </div>

                            {/* URL Type */}
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                                <label style={{ width: '100px', marginRight: '10px' }}>URL Type:</label>
                                <select
                                    name="urlType"
                                    value={targetCompanyUrl.urlType}
                                    onChange={this.handleInputChange}
                                    required
                                    style={{ width: '250px', height: '30px' }}
                                >
                                    <option value="">-- Select Type --</option>
                                    <option value="Decision Studio">Decision Studio</option>
                                    <option value="Case Manager">Case Manager</option>
                                </select>
                            </div>

                            {/* URL */}
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                                <label style={{ width: '100px', marginRight: '10px' }}>URL:</label>
                                <input
                                    type="text"
                                    name="url"
                                    value={targetCompanyUrl.url}
                                    onChange={this.handleInputChange}
                                    required
                                    style={{ flex: 1 }}
                                />
                            </div>

                            {/* Buttons */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginBottom: '15px' }}>
                                <input
                                    type="submit"
                                    value={isEditing ? "Update" : "Add"}
                                    className="btn btn-primary"
                                />
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() =>
                                        this.setState({ targetCompanyUrl: this.createEmptyUrl(), isEditing: false })
                                    }
                                >
                                    Clear
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={this.props.toggleDialog}
                                >
                                    Close
                                </button>
                            </div>

                            <hr />

                            {/* Table */}
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>URL Name</th>
                                        <th>Type</th>
                                        <th>URL</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {companyUrlList.map(url => (
                                        <tr key={url.id}>
                                            <td>{url.urlName}</td>
                                            <td>{url.urlType}</td>
                                            <td>{url.url}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    onClick={() => this.handleEdit(url)}
                                                    className="btn btn-sm btn-info"
                                                    style={{ marginRight: "5px" }}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => this.handleDelete(url.id)}
                                                    className="btn btn-sm btn-danger"
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </form>
                    </Dialog>

                    {this.state.showDeleteConfirm && (
                        <Dialog
                            title="Please confirm URL deletion"
                            onClose={() => this.setState({ showDeleteConfirm: false, deleteTargetId: null })}
                        >
                            <div style={{ margin: '20px 0', textAlign: 'center' }}>
                                Are you sure you want to delete this URL?
                            </div>
                           <div style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
                                <button
                                    className="cancel-button"
                                    onClick={() => this.setState({ showDeleteConfirm: false, deleteTargetId: null })}
                                >
                                    No
                                </button>
                                <button
                                    className="confirm-button"
                                    onClick={this.confirmDelete}
                                >
                                    Yes
                                </button>
                            </div>

                        </Dialog>
                    )}
                </>
            );
        }
    }
export default CompanyUrlsForm;
